import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { getAuth } from '@angular/fire/auth';
import { firstValueFrom } from 'rxjs';
import { saveAs } from 'file-saver';
import { environment } from 'src/environments/environment';
import { AppCheckService } from './app-check.service';
import { SettingsService } from './settings.service';
import { ImportedTridy } from './tridys.service';

export type TridyQrFormat = 'cmyk-pdf' | 'cmyk-jpeg' | 'png' | 'jpeg';
export type TridyQrErrorCorrection = 'L' | 'M' | 'Q' | 'H';

export interface TridyQrExportSettings {
  format: TridyQrFormat;
  widthMm: number;
  dpi: number;
  quietZoneModules: number;
  errorCorrection: TridyQrErrorCorrection;
  jpegQuality: number;
  filenamePrefix?: string;
  filenameTemplate?: string;
}

@Injectable({ providedIn: 'root' })
export class TridyQrExportService {
  constructor(
    private http: HttpClient,
    private appCheckService: AppCheckService,
    private settingsService: SettingsService
  ) {}

  async downloadFromBackend(
    tridys: ImportedTridy[],
    settings: TridyQrExportSettings
  ): Promise<void> {
    if (!tridys.length) return;

    const headers: Record<string, string> = {};
    const authToken = await getAuth().currentUser?.getIdToken();
    const appCheckToken = await this.appCheckService.getToken();
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    if (appCheckToken) headers['X-Firebase-AppCheck'] = appCheckToken;

    const tridyIds = tridys.map(tridy => String(tridy?.id || '').trim()).filter(Boolean);
    if (!tridyIds.length) throw new Error('No valid DPP IDs selected for backend QR export.');

    const filenameTemplate = settings.filenameTemplate?.trim();
    const payload = {
      contextId: this.settingsService.contextId$,
      tridyIds,
      format: settings.format,
      widthMm: Math.max(10, Number(settings.widthMm) || 30),
      dpi: Math.max(72, Number(settings.dpi) || 300),
      errorCorrection: settings.errorCorrection,
      quietZoneModules: Math.max(4, Number(settings.quietZoneModules) || 4),
      jpegQuality: Math.max(1, Math.min(100, Number(settings.jpegQuality) || 95)),
      ...(filenameTemplate ? { filenameTemplate } : {})
    };

    const response = await firstValueFrom(
      this.http.post(this.backendQrCodeUrl, payload, {
        headers,
        observe: 'response',
        responseType: 'blob'
      })
    );

    const blob = response.body;
    if (!blob) throw new Error('Backend QR export returned an empty response.');

    const backendName = this.getDownloadFileName(response.headers.get('content-disposition'));
    const extension = backendName?.toLowerCase().endsWith('.zip')
      ? 'zip'
      : this.getBackendDownloadExtension(response.headers.get('content-type'), settings.format);
    const prefix = this.sanitizeFilePart(settings.filenamePrefix || 'dpp-qr-codes') || 'dpp-qr-codes';
    const now = new Date();
    const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const generatedName = `${prefix}-${date}-${tridyIds.length}.${extension}`;
    const singleName = tridyIds.length === 1 ? `${this.sanitizeFilePart(tridyIds[0])}.${extension}` : generatedName;
    const downloadName = extension === 'zip' ? generatedName : backendName || singleName;
    saveAs(blob, downloadName);
  }

  private getFormatExtension(format: TridyQrFormat): string {
    if (format === 'cmyk-pdf') return 'pdf';
    if (format === 'cmyk-jpeg') return 'jpg';
    return format === 'jpeg' ? 'jpg' : 'png';
  }

  private get backendQrCodeUrl(): string {
    const configuredBaseUrl = (environment as any).apiUrl || (environment as any).apiBaseUrl || (environment as any).apiHost;
    const rawBaseUrl = String(configuredBaseUrl || `https://${environment.firebase.projectId}.firebaseapp.com`).replace(/\/$/, '');
    const baseUrl = rawBaseUrl.startsWith('http') ? rawBaseUrl : `https://${rawBaseUrl}`;
    return `${baseUrl}/v1/qr-code`;
  }

  private getBackendDownloadExtension(contentType: string | null, format: TridyQrFormat): string {
    if (contentType?.includes('zip')) return 'zip';
    if (contentType?.includes('pdf')) return 'pdf';
    if (contentType?.includes('jpeg')) return 'jpg';
    if (contentType?.includes('png')) return 'png';
    return this.getFormatExtension(format);
  }

  private getDownloadFileName(contentDisposition: string | null): string | undefined {
    if (!contentDisposition) return undefined;
    const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1].replace(/"/g, ''));
    const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return asciiMatch?.[1];
  }

  private sanitizeFilePart(value: string): string {
    return value.replace(/[\\/:*?"<>|]+/g, '-').trim();
  }
}
